/**
 * Asb606AdminArchive — ASB606 admin platform archive hub.
 * Uses unified DashboardLayout (header + breadcrumbs + back-btn).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Users, FileText } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

const CARDS = [
  {
    id: 'supervisors',
    nameAr: 'أرشيف المشرفين والموظفين',
    nameEn: 'Supervisors & Staff Archive',
    descAr: 'سجلّات المشرفين المؤرشفة (قابلة للاستعادة أو الحذف النهائي)',
    descEn: 'Archived supervisor records (restore or hard-delete)',
    icon: FileText,
    color: '#f59e0b',
    route: '/control/archive/asb606-admin/supervisors',
  },
  {
    id: 'admins',
    nameAr: 'أرشيف حسابات الإدارة',
    nameEn: 'Admin Accounts Archive',
    descAr: 'حسابات الموظفين والأدمن المؤرشفة (قابلة للاستعادة أو الحذف النهائي)',
    descEn: 'Archived staff & admin accounts (restore or hard-delete)',
    icon: Users,
    color: '#3b82f6',
    route: '/control/archive/asb606-admin/admins',
  },
];

export default function Asb606AdminArchive() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const [search, setSearch] = useState('');

  const filtered = CARDS.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [c.nameAr, c.nameEn, c.descAr, c.descEn].some((t) => t.toLowerCase().includes(q));
  });

  return (
    <DashboardLayout
      title="أرشيف ASB606 ADMIN"
      titleEn="ASB606 ADMIN Archive"
      subtitle="اختر فئة الأرشيف التي تريد إدارتها"
      subtitleEn="Pick the archive category you want to manage"
      screenCode="ARC-002"
      backPath="/control/archive"
      onBack={() => navigate('/control/archive')}
      breadcrumb={[
        { labelAr: 'الأرشيف', labelEn: 'Archive', path: '/control/archive' },
        { labelAr: 'ASB606 ADMIN', labelEn: 'ASB606 ADMIN' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="asb606-admin-archive">
        {/* Search bar */}
        <div className="relative max-w-xl mb-6">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={language === 'ar' ? 'بحث في فئات الأرشيف...' : 'Search archive categories...'}
            className="w-full ps-3 pe-9 py-3 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm focus:border-[#d4af37]/50 focus:outline-none"
            data-testid="archive-search-input"
          />
        </div>

        {/* Cards grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-[#808080]">
            <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>{language === 'ar' ? 'لا توجد نتائج' : 'No matches'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {visiblePages(filtered, 'route').map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.button
                  key={card.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  whileHover={{ y: -5 }}
                  onClick={() => navigate(card.route)}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-start transition-all hover:border-[#d4af37]/40"
                  data-testid={`archive-card-${card.id}`}
                >
                  <div
                    className="w-14 h-14 rounded-xl mb-3 flex items-center justify-center border"
                    style={{ backgroundColor: `${card.color}15`, borderColor: `${card.color}40` }}
                  >
                    <Icon className="h-7 w-7" style={{ color: card.color }} />
                  </div>
                  <h3 className="text-base font-bold text-white mb-1">
                    {language === 'ar' ? card.nameAr : card.nameEn}
                  </h3>
                  <p className="text-xs text-[#71717A] leading-relaxed">
                    {language === 'ar' ? card.descAr : card.descEn}
                  </p>
                </motion.button>
              );
            })}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
